<div class="migrate-sites-container">
  <div class="page-header">
    <div class="title-large" data-testid="migrateSitesTitle">Migrate Sites</div>
    <div class="body-medium text-muted">
      Search and manage charging sites migrated to the new platform
    </div>
  </div>

  <div class="panels-grid">
    <!-- LEFT PANEL: Search Sites -->
    <div class="panel card">
      <div class="panel-header">
        <div class="title-medium">
          <span class="material-symbols-rounded icon-header">search</span>
          Search Sites
        </div>
      </div>

      <div class="search-box">
        <div class="input-wrapper">
          <span class="material-symbols-rounded input-icon">search</span>
          <input
            type="text"
            [formControl]="searchControl"
            placeholder="Search by city or site name (min 2 chars)..."
            data-testid="searchSiteInput"
          />
          @if (searchControl.value) {
            <button
              type="button"
              class="clear-btn"
              (click)="clearSearch()"
              title="Clear search"
            >
              <span class="material-symbols-rounded">close</span>
            </button>
          }
        </div>
      </div>

      @if (isSearching()) {
        <div class="loading-state">
          <mat-spinner diameter="36"></mat-spinner>
          <span class="body-medium text-muted">Searching sites...</span>
        </div>
      } @else if (searchError()) {
        <div class="error-state">
          <span class="material-symbols-rounded text-danger">error</span>
          <span class="body-medium text-danger">{{ searchError() }}</span>
        </div>
      } @else if (hasSearched() && searchResults().length === 0) {
        <div class="empty-state">
          <span class="material-symbols-rounded empty-icon">location_off</span>
          <div class="body-large">No sites found</div>
          <div class="body-medium text-muted">Try a different city or site name.</div>
        </div>
      } @else if (!hasSearched()) {
        <div class="empty-state info-state">
          <span class="material-symbols-rounded empty-icon">travel_explore</span>
          <div class="body-large">Search for a site</div>
          <div class="body-medium text-muted">
            Enter a city name (e.g. Halmstad, Stockholm) to find charging sites.
          </div>
        </div>
      } @else {
        <div class="site-list" data-testid="searchResultsList">
          @for (site of searchResults(); track site.id) {
            <div
              class="site-card"
              [class.selected]="selectedSite()?.id === site.id"
              [class.already-migrated]="isSiteMigrated(site.id)"
              (click)="selectSite(site)"
              matRipple
            >
              <div class="site-card-content">
                <div class="site-header">
                  <span class="material-symbols-rounded site-icon">location_on</span>
                  <div class="site-name">
                    {{ site.name || site.shortAddress || 'Site ' + site.id }}
                  </div>
                  @if (isSiteMigrated(site.id)) {
                    <span class="badge badge-migrated">
                      <span class="material-symbols-rounded badge-icon">check</span>
                      Migrated
                    </span>
                  }
                </div>

                <div class="site-meta">
                  <span class="meta-item site-id">
                    <strong>ID:</strong> <code class="code-id">{{ site.id }}</code>
                  </span>
                  @if (site.shortAddress && site.name && site.shortAddress !== site.name) {
                    <span class="meta-item site-address-item">
                      {{ site.shortAddress }}
                    </span>
                  }
                  @if (site.coordinates) {
                    <span class="meta-item">
                      <span class="material-symbols-rounded meta-icon">explore</span>
                      {{ site.coordinates.lat | number:'1.4-4' }}, {{ site.coordinates.lng | number:'1.4-4' }}
                    </span>
                  }
                </div>
              </div>

              <div class="site-card-actions">
                @if (isSiteMigrated(site.id)) {
                  <button
                    type="button"
                    class="icon-btn added"
                    disabled
                    title="Already in Migrated Sites"
                  >
                    <span class="material-symbols-rounded">check</span>
                  </button>
                } @else {
                  <button
                    type="button"
                    class="icon-btn primary"
                    (click)="addToMigratedSites(site, $event)"
                    [disabled]="isAddingSiteId() === site.id"
                    [attr.data-testid]="'addSiteBtn_' + site.id"
                    title="Add to Migrated Sites"
                  >
                    @if (isAddingSiteId() === site.id) {
                      <mat-spinner diameter="18"></mat-spinner>
                    } @else {
                      <span class="material-symbols-rounded">add</span>
                    }
                  </button>
                }
              </div>
            </div>
          }
        </div>
      }
    </div>

    <!-- RIGHT PANEL: Migrated Sites -->
    <div class="panel card">
      <div class="panel-header">
        <div class="title-medium">
          <span class="material-symbols-rounded icon-header">playlist_add_check</span>
          Migrated Sites
          <span class="count-badge" data-testid="migratedCount">
            {{ migratedSites().length }}
          </span>
        </div>
        <button
          type="button"
          class="icon-button-small"
          (click)="loadMigratedSites()"
          [disabled]="isLoadingMigrated()"
          title="Refresh migrated sites list"
        >
          <span class="material-symbols-rounded" [class.spin]="isLoadingMigrated()">refresh</span>
        </button>
      </div>

      <div class="search-box">
        <div class="input-wrapper">
          <span class="material-symbols-rounded input-icon">filter_list</span>
          <input
            type="text"
            [formControl]="filterMigratedControl"
            placeholder="Filter migrated sites by name or ID..."
            data-testid="filterMigratedInput"
          />
          @if (filterMigratedControl.value) {
            <button
              type="button"
              class="clear-btn"
              (click)="filterMigratedControl.setValue('')"
              title="Clear filter"
            >
              <span class="material-symbols-rounded">close</span>
            </button>
          }
        </div>
      </div>

      @if (isLoadingMigrated()) {
        <div class="loading-state">
          <mat-spinner diameter="36"></mat-spinner>
          <span class="body-medium text-muted">Loading migrated sites...</span>
        </div>
      } @else if (migratedError()) {
        <div class="error-state">
          <span class="material-symbols-rounded text-danger">error</span>
          <span class="body-medium text-danger">{{ migratedError() }}</span>
          <button class="secondary-button" (click)="loadMigratedSites()">Retry</button>
        </div>
      } @else if (migratedSites().length === 0) {
        <div class="empty-state">
          <span class="material-symbols-rounded empty-icon">assignment_late</span>
          <div class="body-large">No migrated sites yet</div>
          <div class="body-medium text-muted">
            Search for a site on the left panel and click "Add to Migrated Sites".
          </div>
        </div>
      } @else if (filteredMigratedSites().length === 0) {
        <div class="empty-state">
          <span class="material-symbols-rounded empty-icon">search_off</span>
          <div class="body-large">No matching migrated sites</div>
          <div class="body-medium text-muted">Try adjusting your filter query.</div>
        </div>
      } @else {
        <div class="site-list" data-testid="migratedSitesList">
          @for (site of filteredMigratedSites(); track site.siteId) {
            <div class="site-card migrated-card" data-testid="migratedSiteItem">
              <div class="site-card-content">
                <div class="site-header">
                  <span class="material-symbols-rounded site-icon">ev_station</span>
                  <div class="site-name">{{ site.name }}</div>
                </div>

                <div class="site-meta">
                  <span class="meta-item">
                    <strong>ID:</strong>
                    <code class="code-id">{{ site.siteId }}</code>
                  </span>
                  @if (site.coordinates) {
                    <span class="meta-item">
                      <span class="material-symbols-rounded meta-icon">explore</span>
                      {{ site.coordinates.lat | number:'1.4-4' }}, {{ site.coordinates.lng | number:'1.4-4' }}
                    </span>
                  } @else if (site.latitude != null && site.longitude != null) {
                    <span class="meta-item">
                      <span class="material-symbols-rounded meta-icon">explore</span>
                      {{ site.latitude | number:'1.4-4' }}, {{ site.longitude | number:'1.4-4' }}
                    </span>
                  }
                </div>
              </div>

              <div class="site-card-actions">
                <button
                  type="button"
                  class="icon-btn danger"
                  (click)="removeFromMigratedSites(site, $event)"
                  [disabled]="isDeletingSiteId() === site.siteId"
                  [attr.data-testid]="'removeSiteBtn_' + site.siteId"
                  title="Remove from Migrated Sites"
                >
                  @if (isDeletingSiteId() === site.siteId) {
                    <mat-spinner diameter="18"></mat-spinner>
                  } @else {
                    <span class="material-symbols-rounded">delete</span>
                  }
                </button>
              </div>
            </div>
          }
        </div>
      }
    </div>
  </div>
</div>
